import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  RemixBuilderProvider,
  RemixBuilderContext,
} from "./shared/RemixBuilderContext";
import CtaRemixBuilder from "./CtaRemixBuilder";
import RemixModalOverlay from "./shared/RemixModalOverlay";
import useRemix from "./shared/useRemix";
import RemixUploader from "./shared/RemixUploader";
import StepRemixBuilder from "./StepRemixBuilder";
import VariantRemixBuilder from "./VariantRemixBuilder";

const RemixBuilder = (props) => {
  const { type = "cta", initialOpen = false, embedInBootstrapModal = false } = props;

  const { Remix } = useRemix({ builder: true });

  const [contextData, builder, setContextData] =
    useContext(RemixBuilderContext);
  const [modalVisibility, setModalVisibility] = useState(initialOpen);
  const remixModal = useRef(null);
  const current_user = contextData.current_user;

  const fetchSectionTemplates = async () => {
    const templates = await Remix.getSectionTemplates();
    setContextData((prevContextData) => ({
      ...prevContextData,
      section_templates: templates,
    }));
  };

  useEffect(() => {
    fetchSectionTemplates();
  }, []);

  const CTA_TYPE_OPTION_MAP = {
    page: "Landing Page",
    overlay: "Overlay Popup",
    hook: "Hook Popup",
    bar: "Sticky Bar",
    inline: "Embed",
  };

  const normalizeCtaType = (ctaType) => {
    const validTypes = Object.keys(CTA_TYPE_OPTION_MAP);
    if (!ctaType) return null;

    if (typeof ctaType === "object" && ctaType.value) {
      return validTypes.includes(ctaType.value) ? ctaType.value : null;
    }

    return String(ctaType)
      .split(",")
      .map((value) => value.trim())
      .find((value) => validTypes.includes(value));
  };

  const setupScratchMode = (ctaType = null) => {
    setContextData((prev) => {
      const normalizedCtaType = normalizeCtaType(ctaType);

      return {
        ...prev,
        from_scratch: true,
        current_step: 2,
        active_funnel_step: 1,
        uploaded_sections: [],
        matched_sections: [],
        uploaded_image_url: null,
        remix_settings: {
          ...prev.remix_settings,
          ...(normalizedCtaType
            ? {
                cta_type: {
                  value: normalizedCtaType,
                  label: CTA_TYPE_OPTION_MAP[normalizedCtaType],
                },
              }
            : {}),
          styles: prev.remix_settings?.styles || {},
        },
      };
    });
  };

  // Initialize font dropdown when builder is visible (embedded or modal) so theme font selectors work
  useEffect(() => {
    if (
      modalVisibility &&
      typeof window.ctas?.builder?.fonts?.initialize === "function" &&
      !window.builder_fonts_dropdown
    ) {
      window.ctas.builder.fonts.initialize();
    }
  }, [modalVisibility]);

  useEffect(() => {
    if (remixModal.current && modalVisibility == true && !embedInBootstrapModal) {
      $(remixModal.current).modal("show");
      $(remixModal.current).addClass("in").css("display", "block");
      $("body").addClass("modal-open");
      $(remixModal.current).parents(".modal").scrollTop(0);
    } else if (remixModal.current && !embedInBootstrapModal) {
      $(remixModal.current).removeClass("in").css("display", "none");
    }
  });

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setModalVisibility(false);
      const bootstrapModal = document.getElementById("variantRemixAiModal");
      if (bootstrapModal && typeof $ !== "undefined") {
        $(bootstrapModal).modal("hide");
      }
    }
  };

  useEffect(() => {
    if (!modalVisibility) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setModalVisibility(false);
        const bootstrapModal = document.getElementById("variantRemixAiModal");
        if (bootstrapModal && typeof $ !== "undefined") {
          $(bootstrapModal).modal("hide");
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [modalVisibility]);

  useEffect(() => {
    const handleOpenFromScratch = (event) => {
      const bootstrapModal = document.getElementById("variantRemixAiModal");
      if (bootstrapModal && typeof $ !== "undefined") {
        $(bootstrapModal).modal("hide");
      }
      setupScratchMode(event?.detail?.cta_type);
      setModalVisibility(true);
    };

    // Turbolinks page caching can leave old listeners alive across navigations.
    // Keep this as a singleton so clicking "Create from Scratch" only opens one modal.
    const existingHandler = window.__variantRemixOpenFromScratchHandler;
    if (existingHandler) {
      document.removeEventListener(
        "variantRemixAiModal:openFromScratch",
        existingHandler
      );
    }

    document.addEventListener("variantRemixAiModal:openFromScratch", handleOpenFromScratch);
    window.__variantRemixOpenFromScratchHandler = handleOpenFromScratch;

    return () =>
      {
        document.removeEventListener(
          "variantRemixAiModal:openFromScratch",
          handleOpenFromScratch
        );

        if (window.__variantRemixOpenFromScratchHandler === handleOpenFromScratch) {
          delete window.__variantRemixOpenFromScratchHandler;
        }
      };
  }, []);

  const modalContentBody = (
    <>
      {contextData.remix_loading?.loading && (
        <div
          className="absolute inset-x-0 bottom-0 -top-[35px] z-10 flex items-center justify-center bg-white dark:bg-slate-950"
          aria-hidden="false"
        >
          <RemixModalOverlay remixLoading={contextData.remix_loading} />
        </div>
      )}
      <div className="flex gap-5 mt-[-35px] px-7 flex-1 min-w-0">
        {type === "cta" && <CtaRemixBuilder rightColumnClassName="pt-[30px]" />}
        {type === "step" && <StepRemixBuilder />}
        {type === "variant" && <VariantRemixBuilder rightColumnClassName="pt-[30px]" />}
      </div>
    </>
  );

  const modalContent = (
    <div
      ref={remixModal}
      className="cf-remix-builder-wrapper-modal remix-editor modal fade"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="myModalLabel"
      aria-modal="true"
      data-backdrop="false"
      style={{
        background: "rgb(0, 0, 0, 0.5)",
        overflowY: "auto",
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="modal-dialog !w-[100%] !max-w-[1600px] !h-[100%] ml-auto mr-auto"
        role="document"
      >
        <div className="modal-content hard-center bg-white dark:bg-slate-950 !shadow-2xl max-h-[95vh] flex flex-column">
          <div className="modal-header !border-slate-200 dark:!border-slate-800">
            <button
              type="button"
              className="close dark:text-white relative z-[12000] pointer-events-auto"
              onClick={() => {
                setModalVisibility(false);
                const bootstrapModal = document.getElementById("variantRemixAiModal");
                if (bootstrapModal && typeof $ !== "undefined") {
                  $(bootstrapModal).modal("hide");
                }
              }}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 17 17"
                fill="none"
              >
                <path
                  d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>

          </div>

          <div className="modal-body flex h-[85vh] relative">
            {modalContentBody}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="remix-editor">
      <RemixUploader
        className={props.className}
        showRemixFromChat={
          type === "cta" &&
          (contextData.current_user?.admin_type ||
            contextData.current_user?.beta === true)
        }
        onRemixFromChat={() => {
          const modal = document.getElementById("variantRemixAiModal");
          if (modal && typeof $ !== "undefined") {
            $(modal).modal("hide");
          }
        }}
        onUploadProcessing={async (imagePath, subtitleRef) => {
          if (type !== "cta") {
            return imagePath;
          }

          subtitleRef.current.textContent = "Generating custom template...";
          setContextData((prevContextData) => ({
            ...prevContextData,
            remix_loading: { loading: true, progress: 0, isAiRemixing: true },
          }));

          try {
            await Remix.importNativeFromImage(
              imagePath,
              "Imported Campaign",
              (progress) => {
                if (subtitleRef.current && progress.label) {
                  subtitleRef.current.textContent = progress.label;
                }
                setContextData((prevContextData) => ({
                  ...prevContextData,
                  remix_loading: {
                    ...(prevContextData.remix_loading || {}),
                    loading: true,
                    isAiRemixing: true,
                    progress: progress.percent,
                    statusMessage: progress.label,
                  },
                }));
              }
            );
            return imagePath;
          } catch (error) {
            setContextData((prevContextData) => ({
              ...prevContextData,
              remix_loading: null,
            }));
            throw error;
          }
        }}
        onUploadSuccess={(uploadedImageUrl) => {
          if (uploadedImageUrl === "scratch") {
            setupScratchMode();
            setModalVisibility(true);
            return;
          }

          if (type === "cta") {
            return;
          }

          setModalVisibility(true);
        }}
        filestackApiKey={contextData.filestack_api_key}
        environment={contextData.environment}
        websiteId={contextData.website.id}
        website={contextData.website}
        subscription={contextData.subscription}
        account={contextData.account}
        current_user={contextData.current_user}
      />

      {modalVisibility == true && embedInBootstrapModal && (
        <div className="remix-editor remix-editor-embedded flex flex-col flex-1 min-h-0 relative">
          {modalContentBody}
        </div>
      )}
      {modalVisibility == true && !embedInBootstrapModal && createPortal(modalContent, document.body)}
    </div>
  );
};

const RemixBuilderWrapper = (props) => {
  const {
    type = "cta",
    website,
    cta = null,
    environment,
    FILESTACK_API_KEY,
    onSubmit,
    subscription,
    account,
    current_user,
  } = props;

  return (
    <RemixBuilderProvider
      type={type}
      website={website}
      cta={cta}
      subscription={subscription}
      account={account}
      current_user={current_user}
      environment={environment}
      FILESTACK_API_KEY={FILESTACK_API_KEY}
      onSubmit={onSubmit}
      remix_mappings={props.remix_mappings}
      styles={props.styles}
      initialSectionTemplates={props.initialSectionTemplates}
      initialFunnelSteps={props.initialFunnelSteps}
      initialRemixPrompt={props.initialRemixPrompt}
      initialCtaType={props.initialCtaType}
    >
      <RemixBuilder
        type={type}
        className={props.className}
        initialOpen={
          !!(
            (props.initialSectionTemplates &&
              props.initialSectionTemplates.length > 0) ||
            (props.initialFunnelSteps && props.initialFunnelSteps.length > 0)
          )
        }
        embedInBootstrapModal={
          !!(
            (props.initialSectionTemplates &&
              props.initialSectionTemplates.length > 0) ||
            (props.initialFunnelSteps && props.initialFunnelSteps.length > 0)
          )
        }
      />
    </RemixBuilderProvider>
  );
};

export default RemixBuilderWrapper;
